import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, FileText, Eye, RefreshCw, Trash2, Layers, FolderDown, Download, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDelete from "@/components/ConfirmDelete";
import { DocDownloadButtons } from "@/components/DocDownloadButtons";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LANGS = [
  { v: "tr", l: "Türkçe" },
  { v: "en", l: "English" },
  { v: "auto", l: "Otomatik / Karışık" },
];

const PROCESSING = ["uploaded", "parsing", "chunking", "embedding", "indexing"];

const STEP_LABEL = {
  uploaded: "Sıraya alındı",
  parsing: "Metin çıkarılıyor",
  chunking: "Parçalara ayrılıyor",
  embedding: "Vektörler hazırlanıyor",
  indexing: "İndeksleniyor",
};

function DocMeta({ doc }) {
  const processing = PROCESSING.includes(doc.status);
  const total = doc.total_segments || 0;
  const done = doc.segment_count || 0;

  if (doc.status === "failed") {
    return <div className="mt-0.5 text-xs text-red-600">Başarısız · {doc.error}</div>;
  }
  if (!processing) {
    return <div className="mt-0.5 text-xs text-muted-foreground">{done} parça</div>;
  }

  const hasProgress = doc.status === "indexing" && total > 0;
  const pct = hasProgress ? Math.min(100, Math.round((done / total) * 100)) : null;
  return (
    <div className="mt-1">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{STEP_LABEL[doc.status] || "İşleniyor"}…</span>
        {hasProgress && (
          <span data-testid={`progress-count-${doc.id}`} className="mono font-medium text-foreground">
            {done} / {total} parça · %{pct}
          </span>
        )}
      </div>
      <div className="mt-1.5 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-secondary">
        {hasProgress ? (
          <div
            data-testid={`progress-bar-${doc.id}`}
            className="h-full rounded-full bg-foreground transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        ) : (
          <div className="h-full w-1/3 animate-pulse rounded-full bg-foreground/50" />
        )}
      </div>
    </div>
  );
}

export default function DatasetPage() {
  const { did } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [dataset, setDataset] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [segments, setSegments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [zipping, setZipping] = useState(false);

  const loadDocs = useCallback(() => {
    api.getDocuments(did).then(setDocs).catch(() => {});
    api.getDataset(did).then(setDataset).catch(() => {});
  }, [did]);

  useEffect(loadDocs, [loadDocs]);

  // poll while processing
  useEffect(() => {
    const anyProcessing = docs.some((d) => PROCESSING.includes(d.status));
    if (!anyProcessing) return;
    const t = setInterval(loadDocs, 2000);
    return () => clearInterval(t);
  }, [docs, loadDocs]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        await api.uploadDocument(did, f);
      }
      toast.success(`${files.length} doküman yüklendi, işleniyor...`);
      loadDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  const openPreview = async (doc) => {
    setPreview(doc);
    setSegments([]);
    const segs = await api.getSegments(doc.id);
    setSegments(segs);
  };

  const onReprocess = async (doc) => {
    await api.reprocess(doc.id);
    toast.success("Yeniden işleme sırasına alındı");
    loadDocs();
  };

  const onDelete = async (doc) => {
    await api.deleteDocument(doc.id);
    toast.success("Doküman silindi");
    loadDocs();
  };

  const downloadAll = async (content) => {
    setZipping(true);
    try {
      const name = await api.downloadDatasetZip(dataset, content);
      toast.success(`${name} indirildi`);
    } catch (e) {
      toast.error(errorDetail(e, "ZIP oluşturulamadı"), { duration: 8000 });
    } finally {
      setZipping(false);
    }
  };

  const changeLanguage = async (lang) => {
    try {
      const d = await api.updateDataset(did, { language: lang });
      setDataset(d);
      toast.success("İçerik dili güncellendi. Mevcut belgeler için 'Yeniden işle' ile dil bilgisini yenileyebilirsiniz.");
    } catch {
      toast.error("Dil güncellenemedi");
    }
  };

  return (
    <div className="fade-up">
      <button
        data-testid="back-project-btn"
        onClick={() => dataset && navigate(`/projects/${dataset.project_id}`)}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Proje
      </button>

      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{dataset?.name || "..."}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {dataset?.stats?.documents || 0} doküman · {dataset?.stats?.segments || 0} parça · {dataset?.stats?.ready || 0} hazır
            {dataset?.stats?.failed ? ` · ${dataset.stats.failed} başarısız` : ""}
          </p>
        </div>
        {dataset && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="download-all-btn"
                  variant="outline" size="sm" className="rounded-full"
                  disabled={docs.length === 0 || zipping}
                >
                  <FolderDown className={`mr-1.5 h-4 w-4 ${zipping ? "animate-pulse" : ""}`} />
                  {zipping ? "ZIP hazırlanıyor..." : "Tümünü indir"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem data-testid="download-all-original" onClick={() => downloadAll("original")}>
                  <Download className="mr-2 h-4 w-4" />
                  <div>
                    <div className="text-sm">Orijinal dosyalar (ZIP)</div>
                    <div className="text-xs text-muted-foreground">Yoksa işlenmiş metin eklenir</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="download-all-text" onClick={() => downloadAll("text")}>
                  <FileDown className="mr-2 h-4 w-4" />
                  <div>
                    <div className="text-sm">İşlenmiş metinler (ZIP)</div>
                    <div className="text-xs text-muted-foreground">Her doküman için .md</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">İçerik dili</span>
              <Select value={dataset.language || "auto"} onValueChange={changeLanguage}>
                <SelectTrigger data-testid="dataset-language-edit" className="h-9 w-40 rounded-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ConfirmDelete
              testId="delete-dataset-dialog"
              title="Veri setini sil?"
              description={`"${dataset.name}" veri seti, tüm dokümanları ve vektörleri kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
              onConfirm={async () => {
                await api.deleteDataset(did);
                toast.success("Veri seti silindi");
                navigate(`/projects/${dataset.project_id}`);
              }}
              trigger={
                <Button data-testid="delete-dataset-btn" variant="outline" size="sm" className="shrink-0 rounded-full text-red-600 hover:bg-red-50 hover:text-red-700">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Veri setini sil
                </Button>
              }
            />
          </div>
        )}
      </div>

      {/* Upload zone */}
      <div
        data-testid="upload-zone"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(Array.from(e.dataTransfer.files)); }}
        className={`mt-6 cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
          dragOver ? "border-foreground bg-secondary" : "border-border bg-background hover:bg-secondary/50"
        }`}
      >
        <input
          ref={fileRef}
          data-testid="file-input"
          type="file"
          multiple
          accept=".pdf,.docx,.xlsx,.txt,.md,.markdown"
          className="hidden"
          onChange={(e) => handleFiles(Array.from(e.target.files))}
        />
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-foreground text-background">
          <Upload className="h-5 w-5" />
        </div>
        <p className="mt-3 font-semibold">{uploading ? "Yükleniyor..." : "Doküman yükleyin"}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Sürükleyip bırakın veya tıklayın · PDF, Word, Excel, TXT, Markdown
        </p>
      </div>

      {/* Documents list */}
      <div className="mt-8 space-y-2">
        {docs.length === 0 ? (
          <div className="soft-card px-6 py-12 text-center text-sm text-muted-foreground">
            Henüz doküman yok.
          </div>
        ) : (
          docs.map((doc) => (
            <div
              key={doc.id}
              data-testid={`document-row-${doc.id}`}
              className="soft-card flex flex-wrap items-center gap-3 px-4 py-3 sm:flex-nowrap sm:gap-4"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary">
                <FileText className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{doc.filename}</div>
                <DocMeta doc={doc} />
              </div>
              <StatusBadge status={doc.status} testId={`status-${doc.id}`} />
              <div className="flex items-center gap-1">
                <DocDownloadButtons doc={doc} />
                <Button
                  data-testid={`preview-${doc.id}`}
                  variant="ghost" size="icon" className="h-8 w-8"
                  disabled={doc.status !== "ready"}
                  onClick={() => openPreview(doc)}
                  title="Parça önizleme"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  data-testid={`reprocess-${doc.id}`}
                  variant="ghost" size="icon" className="h-8 w-8"
                  onClick={() => onReprocess(doc)}
                  title="Yeniden işle"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <Button
                  data-testid={`delete-${doc.id}`}
                  variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700"
                  onClick={() => onDelete(doc)}
                  title="Sil"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Chunk preview */}
      <Sheet open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="chunk-preview-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" /> Parça Önizleme
            </SheetTitle>
          </SheetHeader>
          <p className="mt-1 truncate text-sm text-muted-foreground">{preview?.filename}</p>
          <div className="mt-4 space-y-3">
            {segments.length === 0 ? (
              <div className="text-sm text-muted-foreground">Yükleniyor...</div>
            ) : (
              segments.map((s) => (
                <div key={s.id} data-testid={`segment-${s.seq}`} className="rounded-xl border border-border p-3">
                  <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
                    <span className="mono">#{s.seq}</span>
                    <span>{s.token_count} token · {s.provenance?.block_type}
                      {s.provenance?.page_start ? ` · s.${s.provenance.page_start}` : ""}
                    </span>
                  </div>
                  {s.provenance?.section_path?.length > 0 && (
                    <div className="mb-1.5 text-xs font-medium text-foreground">
                      {s.provenance.section_path.join(" › ")}
                    </div>
                  )}
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{s.text}</p>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
